import { parseUrlContext } from '../../routing/contextUrl';

/** @param {object|null|undefined} u */
function pickUserIdFromUserBlob(u) {
    if (!u || typeof u !== 'object') {
        return null;
    }
    const raw = u.id != null ? u.id : u._id;
    if (raw == null || raw === '') {
        return null;
    }
    return String(raw);
}

/** @param {object|null|undefined} u */
function pickInstanceIdFromUserBlob(u) {
    const ti = u?.tkt_instance;
    if (!ti || typeof ti !== 'object') {
        return 'none';
    }
    const raw = ti.id != null ? ti.id : ti._id;
    if (raw == null || raw === '') {
        return 'none';
    }
    return String(raw);
}

/**
 * Prefer the instance encoded in the tab URL so prefs stay tab-local when
 * another tab switches the persisted user instance.
 *
 * @param {object|null|undefined} u localStorage user blob
 * @returns {string}
 */
function pickInstanceIdForActiveTab(u) {
    if (typeof window === 'undefined') {
        return pickInstanceIdFromUserBlob(u);
    }
    try {
        const slug = parseUrlContext(window.location.pathname).slug;
        if (slug && u?.tkt_instance?.slug === slug) {
            return pickInstanceIdFromUserBlob(u);
        }
        if (slug && Array.isArray(u?.tkt_instances)) {
            const match = u.tkt_instances.find((i) => i?.slug === slug);
            const raw = match?.id != null ? match.id : match?._id;
            if (raw != null && raw !== '') {
                return String(raw);
            }
        }
        // URL slug differs from persisted user: keep prefs keyed by slug string
        // until Redux/list resolves a real id (still isolates tabs).
        if (slug) {
            return `slug:${slug}`;
        }
    } catch {
        // fall through
    }
    return pickInstanceIdFromUserBlob(u);
}

/**
 * Read Ticketack session identity from the same `user` blob as `redux/modules/auth.js`.
 * Used before the Redux store exists (bootstrap migration) and for prefs scoping.
 *
 * @returns {{ userId: string|null, instanceId: string }}
 */
export function readPersistedSessionIds() {
    if (typeof window === 'undefined' || !window.localStorage) {
        return { userId: null, instanceId: 'none' };
    }
    try {
        const raw = window.localStorage.getItem('user');
        if (!raw || raw === 'null') {
            return { userId: null, instanceId: 'none' };
        }
        const u = JSON.parse(raw);
        return {
            userId: pickUserIdFromUserBlob(u),
            instanceId: pickInstanceIdForActiveTab(u),
        };
    } catch {
        return { userId: null, instanceId: 'none' };
    }
}

/**
 * Same id rules as `readPersistedSessionIds`, for a live Redux `auth.user` object.
 * @param {object|null|undefined} authUser
 * @returns {string|null}
 */
export function pickUserIdFromAuthUser(authUser) {
    return pickUserIdFromUserBlob(authUser);
}

/**
 * @param {object|null|undefined} authUser
 * @returns {string}
 */
export function pickInstanceIdFromAuthUser(authUser) {
    return pickInstanceIdForActiveTab(authUser);
}
